import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LEGAL_ROUTES } from "../constants/legal";
import "./CookieBanner.css";

const LANGUAGE_KEY = "zedro_academy_language";
const CONSENT_KEY = "cookieConsent";
const META_PIXEL_ID = "2909458706068997";

const DEFAULT_PREFERENCES = {
  essential: true,
  analytics: false,
  marketing: false,
};

const getInitialLanguage = () => {
  if (typeof window === "undefined") return "es";
  return localStorage.getItem(LANGUAGE_KEY) || "es";
};

const getSavedPreferences = () => {
  if (typeof window === "undefined") {
    return DEFAULT_PREFERENCES;
  }

  try {
    const stored = localStorage.getItem(CONSENT_KEY);

    if (!stored) {
      return DEFAULT_PREFERENCES;
    }

    const parsed = JSON.parse(stored);

    return {
      essential: true,
      analytics: Boolean(parsed?.prefs?.analytics),
      marketing: Boolean(parsed?.prefs?.marketing),
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
};

const updateGoogleConsent = ({ analytics, marketing }) => {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: "zedro_consent_update",
    consent_analytics: analytics ? "granted" : "denied",
    consent_marketing: marketing ? "granted" : "denied",
  });

  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: analytics ? "granted" : "denied",
      ad_storage: marketing ? "granted" : "denied",
      ad_user_data: marketing ? "granted" : "denied",
      ad_personalization: marketing ? "granted" : "denied",
      functionality_storage: "granted",
      security_storage: "granted",
    });
  }
};

const trackMetaPageView = () => {
  if (
    typeof window === "undefined" ||
    typeof window.fbq !== "function"
  ) {
    return;
  }

  const path =
    window.location.pathname +
    window.location.search +
    window.location.hash;

  if (window.__ZEDRO_META_LAST_PAGEVIEW__ === path) {
    return;
  }

  window.fbq("track", "PageView");
  window.__ZEDRO_META_LAST_PAGEVIEW__ = path;
};

const initializeMetaPixel = () => {
  if (typeof window === "undefined") return;

  if (window.fbq) {
    window.fbq("consent", "grant");

    if (!window.__ZEDRO_META_PIXEL_INITIALIZED__) {
      window.fbq("init", META_PIXEL_ID);
      window.__ZEDRO_META_PIXEL_INITIALIZED__ = true;
    }

    trackMetaPageView();
    return;
  }

  const fbq = function (...args) {
    fbq.callMethod
      ? fbq.callMethod.apply(fbq, args)
      : fbq.queue.push(args);
  };

  window.fbq = fbq;
  window._fbq = fbq;

  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];

  const script = document.createElement("script");
  script.async = true;
  script.src =
    "https://connect.facebook.net/en_US/fbevents.js";
  script.id = "zedro-meta-pixel-script";

  const firstScript =
    document.getElementsByTagName("script")[0];

  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(
      script,
      firstScript
    );
  } else {
    document.head.appendChild(script);
  }

  fbq("consent", "grant");
  fbq("init", META_PIXEL_ID);

  window.__ZEDRO_META_PIXEL_INITIALIZED__ = true;

  trackMetaPageView();
};

const revokeMetaConsent = () => {
  if (typeof window === "undefined") return;

  if (typeof window.fbq === "function") {
    window.fbq("consent", "revoke");
  }
};

const applyConsent = (preferences) => {
  updateGoogleConsent(preferences);

  if (preferences.marketing) {
    initializeMetaPixel();
  } else {
    revokeMetaConsent();
  }

  window.dispatchEvent(
    new CustomEvent("zedro:consentchange", {
      detail: {
        preferences,
      },
    })
  );
};

const copy = {
  es: {
    aria: "Aviso de cookies",
    title: "Cookies en ZEDRO Academy",
    textStart:
      "Usamos cookies esenciales para que el sitio funcione y, con tu permiso, cookies para analítica y personalización. Puedes aceptarlas, rechazarlas o configurar tus preferencias. Consulta nuestra",
    link: "Política de cookies",
    accept: "Aceptar",
    reject: "Rechazar",
    settings: "Configurar",
    settingsTitle: "Preferencias de cookies",
    settingsText:
      "Puedes ajustar tus preferencias. Las cookies esenciales permanecen activas porque son necesarias para el funcionamiento del sitio.",
    essential: "Cookies esenciales",
    essentialNote:
      "Necesarias para seguridad, sesión y funcionamiento básico.",
    analytics: "Cookies de analítica",
    analyticsNote:
      "Nos ayudan a entender el uso del sitio para mejorar ZEDRO Academy.",
    marketing: "Cookies de personalización y comunicación",
    marketingNote:
      "Nos ayudan a mejorar la experiencia y la comunicación del sitio.",
    save: "Guardar preferencias",
    back: "Volver",
  },
  fr: {
    aria: "Avis sur les cookies",
    title: "Cookies sur ZEDRO Academy",
    textStart:
      "Nous utilisons des cookies essentiels au fonctionnement du site et, avec votre autorisation, des cookies d’analyse et de personnalisation. Vous pouvez les accepter, les refuser ou configurer vos préférences. Consultez notre",
    link: "Politique de cookies",
    accept: "Accepter",
    reject: "Refuser",
    settings: "Configurer",
    settingsTitle: "Préférences de cookies",
    settingsText:
      "Vous pouvez ajuster vos préférences. Les cookies essentiels restent activés, car ils sont nécessaires au fonctionnement du site.",
    essential: "Cookies essentiels",
    essentialNote:
      "Nécessaires à la sécurité, à la session et au fonctionnement de base.",
    analytics: "Cookies d’analyse",
    analyticsNote:
      "Ils nous aident à comprendre l’utilisation du site pour améliorer ZEDRO Academy.",
    marketing: "Cookies de personnalisation et de communication",
    marketingNote:
      "Ils nous aident à améliorer l’expérience et la communication du site.",
    save: "Enregistrer les préférences",
    back: "Retour",
  },
};

export default function CookieBanner({ onOpenSettings }) {
  const [visible, setVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [language, setLanguage] = useState(getInitialLanguage);
  const [preferences, setPreferences] = useState(getSavedPreferences);

  const t = copy[language] || copy.es;

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);

    if (!stored) {
      setVisible(true);
      return;
    }

    applyConsent(getSavedPreferences());
  }, []);

  useEffect(() => {
    const syncLanguage = (event) => {
      const nextLanguage =
        event?.detail?.language ||
        localStorage.getItem(LANGUAGE_KEY) ||
        "es";

      setLanguage(nextLanguage);
    };

    window.addEventListener("zedro:languagechange", syncLanguage);
    window.addEventListener("storage", syncLanguage);

    return () => {
      window.removeEventListener("zedro:languagechange", syncLanguage);
      window.removeEventListener("storage", syncLanguage);
    };
  }, []);

  useEffect(() => {
    const openCookieSettings = () => {
      setPreferences(getSavedPreferences());
      setSettingsOpen(true);
      setVisible(true);
    };

    window.addEventListener(
      "open-cookie-settings",
      openCookieSettings
    );

    return () => {
      window.removeEventListener(
        "open-cookie-settings",
        openCookieSettings
      );
    };
  }, []);

  const saveConsent = (choice, prefs) => {
    const normalizedPreferences = {
      essential: true,
      analytics: Boolean(prefs.analytics),
      marketing: Boolean(prefs.marketing),
    };

    const record = {
      choice,
      ts: new Date().toISOString(),
      prefs: normalizedPreferences,
    };

    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify(record)
    );

    setPreferences(normalizedPreferences);
    applyConsent(normalizedPreferences);
    setVisible(false);
    setSettingsOpen(false);
  };

  const handleChoice = (choice) => {
    if (choice === "custom") {
      onOpenSettings?.();
      setPreferences(getSavedPreferences());
      setSettingsOpen(true);
      setVisible(true);
      return;
    }

    saveConsent(
      choice,
      choice === "all"
        ? {
            essential: true,
            analytics: true,
            marketing: true,
          }
        : {
            essential: true,
            analytics: false,
            marketing: false,
          }
    );
  };

  const handlePreferenceChange = (key) => {
    setPreferences((current) => ({
      ...current,
      [key]: !current[key],
      essential: true,
    }));
  };

  if (!visible) return null;

  return (
    <div
      className="cb-wrap"
      role="dialog"
      aria-live="polite"
      aria-label={t.aria}
    >
      <div className="cb-card">
        <div className="cb-content">
          <div className="cb-title">
            {settingsOpen ? t.settingsTitle : t.title}
          </div>

          {!settingsOpen ? (
            <p className="cb-text">
              {t.textStart}{" "}
              <Link
                className="cb-link"
                to={LEGAL_ROUTES.cookies}
                state={{ from: "public" }}
              >
                {t.link}
              </Link>
              .
            </p>
          ) : (
            <div className="cb-text">
              <p>{t.settingsText}</p>

              <label
                style={{
                  display: "block",
                  marginTop: "12px",
                }}
              >
                <input type="checkbox" checked disabled />{" "}
                {t.essential}
              </label>

              <small>{t.essentialNote}</small>

              <label
                style={{
                  display: "block",
                  marginTop: "12px",
                }}
              >
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={() =>
                    handlePreferenceChange("analytics")
                  }
                />{" "}
                {t.analytics}
              </label>

              <small>{t.analyticsNote}</small>

              <label
                style={{
                  display: "block",
                  marginTop: "12px",
                }}
              >
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={() =>
                    handlePreferenceChange("marketing")
                  }
                />{" "}
                {t.marketing}
              </label>

              <small>{t.marketingNote}</small>
            </div>
          )}
        </div>

        <div className="cb-actions">
          {!settingsOpen ? (
            <>
              <button
                type="button"
                className="cb-btn cb-btn--primary"
                onClick={() => handleChoice("all")}
              >
                {t.accept}
              </button>

              <button
                type="button"
                className="cb-btn cb-btn--secondary"
                onClick={() => handleChoice("reject")}
              >
                {t.reject}
              </button>

              <button
                type="button"
                className="cb-btn cb-btn--ghost"
                onClick={() => handleChoice("custom")}
              >
                {t.settings}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="cb-btn cb-btn--primary"
                onClick={() =>
                  saveConsent("custom", preferences)
                }
              >
                {t.save}
              </button>

              <button
                type="button"
                className="cb-btn cb-btn--ghost"
                onClick={() => setSettingsOpen(false)}
              >
                {t.back}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}